export interface Sample { t: number; x: number; y: number; z: number; yaw: number }

/** Shortest-arc interpolation in degrees; result normalised to [0, 360). */
export function lerpAngleDeg(a: number, b: number, t: number): number {
  let d = (((b - a) % 360) + 360) % 360; // 0..360
  if (d > 180) d -= 360;                  // -180..180, +180 wins the tie
  return (((a + d * t) % 360) + 360) % 360;
}

/** Keeps ~1 s of samples and interpolates at a render time in the past. */
export class InterpBuffer {
  private samples: Sample[] = [];

  push(s: Sample): void {
    const last = this.samples[this.samples.length - 1];
    if (last && s.t <= last.t) return; // out of order or duplicate
    this.samples.push(s);
  }

  prune(before: number): void {
    // Drop samples older than `before` but always keep the newest one: render time trails `now`
    // by far less than the prune window, so the interval being interpolated is never cut.
    while (this.samples.length > 1 && this.samples[0]!.t < before) this.samples.shift();
  }

  sample(t: number): Sample | null {
    const n = this.samples.length;
    if (n === 0) return null;
    if (t >= this.samples[n - 1]!.t) return this.samples[n - 1]!;
    if (t <= this.samples[0]!.t) return this.samples[0]!;
    for (let i = n - 2; i >= 0; i--) {
      const a = this.samples[i]!, b = this.samples[i + 1]!;
      if (t >= a.t) {
        const f = (t - a.t) / (b.t - a.t);
        return { t, x: a.x + (b.x - a.x) * f, y: a.y + (b.y - a.y) * f, z: a.z + (b.z - a.z) * f, yaw: lerpAngleDeg(a.yaw, b.yaw, f) };
      }
    }
    return this.samples[0]!;
  }
}
